import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import {
  animate,
  DragControls,
  motion,
  MotionStyle,
  MotionValue,
  PanInfo,
  useDragControls,
  useMotionValue,
  useReducedMotion,
  useTransform,
} from 'motion/react';

import {
  RCModalBase,
  RCModalBaseRootProps,
  useRCModalBaseContext,
} from '../internalComponents/RCModalBase/RCModalBase';
import { RCCloseButton } from '../RCCloseButton/RCCloseButton';
import { RCThemeOverride } from '../RCThemeContext/RCThemeOverride';

interface RCLightboxProps {
  /** Controls whether the lightbox is open, paired with `onOpenChange`. */
  isOpen: RCModalBaseRootProps['isOpen'];
  /** Called with `false` when the close button or Escape closes the lightbox. */
  onOpenChange: RCModalBaseRootProps['onOpenChange'];
  /** Names the dialog for screen readers, such as the listing or product being viewed. It isn't shown. */
  label: string;
  /** The media to view. */
  children: React.ReactNode;
  /** `dark` (the default) keeps the frame dark in either page theme; `inherit` follows the page theme. */
  theme?: 'dark' | 'inherit';
  /**
   * The frame's surface: `primary` (the default); `recessed` to set media apart from content around it; or
   * `transparent` to show the page behind the media, dimmed by the modal's overlay.
   */
  background?: 'primary' | 'recessed' | 'transparent';
  /**
   * Closes the lightbox when it's dragged down with one finger: the content follows the finger, or only
   * `RCLightbox.SwipeTarget` if there is one, and the frame's background fades to show the page. Vertical
   * touch scrolling is off while it's set, so leave it off for content that's taller than the viewport. A
   * drag that starts inside something that scrolls vertically, such as a list of thumbnails, scrolls it
   * and never closes. Give a sideways scroller inside it `touch-action: pan-x pinch-zoom`, since a
   * scroller sets the touch behavior for what's inside it.
   */
  closeOnSwipeDown?: boolean;
}

// how far a release has to travel, or how fast, to close
const CLOSE_DISTANCE = 100;
const CLOSE_VELOCITY = 500;
// how far a finger moves before it counts as a swipe down, or as something else
const SWIPE_INTENT_DISTANCE = 10;
// how far a drag goes before the swipe target has shrunk all it will, and to what scale
const SHRINK_DISTANCE = 400;
const SHRINK_SCALE = 0.8;

interface SwipeContextValue {
  y: MotionValue<number>;
  dragControls: DragControls;
  onDragEnd: (e: PointerEvent, info: PanInfo) => void;
  setHasTarget: (hasTarget: boolean) => void;
  setPaused: (paused: boolean) => void;
}

const SwipeContext = React.createContext<SwipeContextValue | null>(null);

/**
 * Whether `target` is inside an element, up to `boundary`, that scrolls vertically, such as a list of
 * thumbnails. A drag down there scrolls it, so it never starts a swipe to close, wherever it's scrolled to.
 */
export function isInVerticalScroller(target: EventTarget | null, boundary: Element) {
  let element = target instanceof Element ? target : null;

  while (element && element !== boundary) {
    const { overflowY } = window.getComputedStyle(element);
    const scrolls = overflowY === 'auto' || overflowY === 'scroll';
    if (scrolls && element.scrollHeight > element.clientHeight) return true;
    element = element.parentElement;
  }

  return false;
}

// down only, with a little give upward; back in place unless it closes
const DRAG_CONSTRAINTS = { top: 0, bottom: 0 };
const DRAG_ELASTIC = { top: 0.05, bottom: 1 };

/**
 * A full-screen frame for viewing media, dark by default: a dialog with a close button, Escape to close, and
 * no layout of its own. It is not a gallery: for a set of images with thumbnails and navigation,
 * use `LightboxImage` from commons.
 *
 * Children fill the frame. It scrolls vertically when they're taller than the viewport, and
 * absolutely positioned children are placed against it.
 *
 * The frame defines an inset area as `--lightbox-inset-top`, `-right` and `-left`:
 * spacing from each edge plus the browser safe area. The close button sits on it; place content
 * and controls against the edges with the same properties so they line up.
 */
export function RCLightbox({
  isOpen,
  onOpenChange,
  label,
  children,
  theme = 'dark',
  background = 'primary',
  closeOnSwipeDown = false,
}: RCLightboxProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <RCModalBase.Root
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      initialFocus={closeButtonRef}
      role="dialog"
      // Nothing outside the frame is visible to click.
      useDismissProps={{ outsidePress: false }}
    >
      <RCModalBase.Content
        className="rc-lightbox"
        displayStyle="fullscreen"
      >
        <Body
          label={label}
          theme={theme}
          background={background}
          closeButtonRef={closeButtonRef}
          closeOnSwipeDown={closeOnSwipeDown}
        >
          {children}
        </Body>
      </RCModalBase.Content>
    </RCModalBase.Root>
  );
}

interface BodyProps {
  label: string;
  theme: RCLightboxProps['theme'];
  background: RCLightboxProps['background'];
  closeButtonRef: React.RefObject<HTMLButtonElement>;
  closeOnSwipeDown: boolean;
  children: React.ReactNode;
}

// Rendered inside the modal so a swipe closes through the same path as the close button and Escape.
function Body({
  label,
  theme,
  background,
  closeButtonRef,
  closeOnSwipeDown,
  children,
}: BodyProps) {
  const { close } = useRCModalBaseContext();
  const dragControls = useDragControls();
  const reducedMotion = useReducedMotion();
  const y = useMotionValue(0);
  const backdropOpacity = useTransform(y, [0, 300], [1, 0]);
  // 0 to 1 over the distance a release needs to close, for what fades as the content is pulled away
  const swipeProgress = useTransform(y, [0, CLOSE_DISTANCE], [0, 1]);
  const touches = useRef(new Set<number>());
  const [hasTarget, setHasTarget] = useState(false);
  // a swipe target can pause swiping while its content takes one-finger drags, such as panning a zoomed photo
  const paused = useRef(false);
  const setPaused = useCallback((value: boolean) => { paused.current = value; }, []);

  // whether a second finger joined the current touch, which then can't close the frame
  const multiTouch = useRef(false);
  // where a lone finger went down, until it's moved far enough to tell whether it's swiping down
  const pendingSwipe = useRef<{ id: number; x: number; y: number } | null>(null);

  // Ends are heard on the window: a finger lifted after the element it touched has left the page never
  // reaches the frame, and would otherwise count as down for good.
  useEffect(() => {
    if (!closeOnSwipeDown) return undefined;

    function handleEnd(e: PointerEvent) {
      touches.current.delete(e.pointerId);
      if (pendingSwipe.current?.id === e.pointerId) pendingSwipe.current = null;
    }

    window.addEventListener('pointerup', handleEnd, true);
    window.addEventListener('pointercancel', handleEnd, true);
    return () => {
      window.removeEventListener('pointerup', handleEnd, true);
      window.removeEventListener('pointercancel', handleEnd, true);
    };
  }, [closeOnSwipeDown]);

  function handlePointerDown(e: React.PointerEvent) {
    if (e.pointerType !== 'touch') return;
    // the first finger of a new touch starts the count over
    if (e.isPrimary) {
      touches.current.clear();
      multiTouch.current = false;
    }
    touches.current.add(e.pointerId);

    if (touches.current.size === 1) {
      const canSwipe = !paused.current && !isInVerticalScroller(e.target, e.currentTarget);
      pendingSwipe.current = canSwipe ? { id: e.pointerId, x: e.clientX, y: e.clientY } : null;
      return;
    }

    // a second finger is a pinch, which belongs to the content or the browser
    multiTouch.current = true;
    pendingSwipe.current = null;
    dragControls.cancel();
    animate(y, 0);
  }

  // The drag starts only once a finger is clearly heading down. Starting it sooner would move the content
  // under a sideways swipe meant for something that scrolls, which can keep the browser from scrolling it.
  function handlePointerMove(e: React.PointerEvent) {
    const pending = pendingSwipe.current;
    if (!pending || pending.id !== e.pointerId) return;

    const dx = e.clientX - pending.x;
    const dy = e.clientY - pending.y;
    if (Math.hypot(dx, dy) < SWIPE_INTENT_DISTANCE) return;

    pendingSwipe.current = null;
    if (dy > Math.abs(dx)) dragControls.start(e);
  }

  function handleDragEnd(_e: PointerEvent, { offset, velocity }: PanInfo) {
    if (multiTouch.current) {
      animate(y, 0);
      return;
    }
    if (offset.y < CLOSE_DISTANCE && velocity.y < CLOSE_VELOCITY) return;

    // carries on from where the finger let go while the frame fades out
    if (!reducedMotion) animate(y, window.innerHeight, { duration: 0.25, ease: [0.4, 0, 1, 1] });
    close();
  }

  const heading = (
    <RCModalBase.Heading className="visually-hidden">
      {label}
    </RCModalBase.Heading>
  );

  // the theme and surface go on the outermost element, so the surface's token resolves in the frame's theme
  const frameClassName = classNames(
    'rc-lightbox__frame',
    { 'rc-lightbox__frame--recessed': background === 'recessed' },
    { 'rc-lightbox__frame--transparent': background === 'transparent' },
  );

  const frame = closeOnSwipeDown ? (
    <div className={classNames(frameClassName, 'rc-lightbox__swipeable')}>
      <motion.div
        className="rc-lightbox__backdrop"
        style={{ opacity: backdropOpacity }}
      />
      <SwipeContext.Provider value={{ y, dragControls, onDragEnd: handleDragEnd, setHasTarget, setPaused }}>
        {/* the body moves itself unless a swipe target takes the drag */}
        <motion.div
          className="rc-lightbox__body"
          drag={hasTarget ? false : 'y'}
          dragListener={false}
          dragControls={hasTarget ? undefined : dragControls}
          dragConstraints={DRAG_CONSTRAINTS}
          dragElastic={DRAG_ELASTIC}
          dragSnapToOrigin
          onDragEnd={hasTarget ? undefined : handleDragEnd}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          style={{
            y: hasTarget ? undefined : y,
            '--lightbox-swipe-progress': swipeProgress,
          } as MotionStyle}
        >
          {heading}
          {children}
          <CloseButton ref={closeButtonRef} />
        </motion.div>
      </SwipeContext.Provider>
    </div>
  ) : (
    <div className={classNames(frameClassName, 'rc-lightbox__body')}>
      {heading}
      {children}
      <CloseButton ref={closeButtonRef} />
    </div>
  );

  return (
    <RCThemeOverride theme="dark" enabled={theme === 'dark'}>
      {frame}
    </RCThemeOverride>
  );
}

const CloseButton = React.forwardRef<HTMLButtonElement>(
  function CloseButton(_props, ref) {
    const { close } = useRCModalBaseContext();

    return (
      <div className="rc-lightbox__close">
        <RCCloseButton
          ref={ref}
          variant="elevated-light"
          size="medium"
          onClick={close}
        />
      </div>
    );
  },
);

interface SwipeTargetProps {
  className?: string;
  /** Pauses swiping down, while the content takes one-finger drags itself, such as panning a zoomed photo. */
  disabled?: boolean;
  children: React.ReactNode;
}

/**
 * With `closeOnSwipeDown`, the part of the content that a swipe down moves, such as the media being viewed,
 * shrinking a little as it's pulled away. The rest stays in place: fade it out with
 * `opacity: calc(1 - var(--lightbox-swipe-progress))`, which goes from 0 to 1 as the swipe nears closing.
 * The swipe starts anywhere in the frame. Without `closeOnSwipeDown`, it's a plain wrapper.
 */
function SwipeTarget({ className = undefined, disabled = false, children }: SwipeTargetProps) {
  const swipe = useContext(SwipeContext);
  const stillY = useMotionValue(0);
  const scale = useTransform(swipe?.y ?? stillY, [0, SHRINK_DISTANCE], [1, SHRINK_SCALE]);
  const setHasTarget = swipe?.setHasTarget;
  const setPaused = swipe?.setPaused;

  useLayoutEffect(() => {
    if (!setHasTarget) return undefined;
    setHasTarget(true);
    return () => setHasTarget(false);
  }, [setHasTarget]);

  useLayoutEffect(() => {
    if (!setPaused) return undefined;
    setPaused(disabled);
    return () => setPaused(false);
  }, [setPaused, disabled]);

  if (!swipe) {
    return <div className={classNames('rc-lightbox__swipe-target', className)}>{children}</div>;
  }

  return (
    <motion.div
      className={classNames('rc-lightbox__swipe-target', className)}
      drag="y"
      dragListener={false}
      dragConstraints={DRAG_CONSTRAINTS}
      dragElastic={DRAG_ELASTIC}
      dragSnapToOrigin
      dragControls={swipe.dragControls}
      onDragEnd={swipe.onDragEnd}
      style={{ y: swipe.y, scale }}
    >
      {children}
    </motion.div>
  );
}

RCLightbox.SwipeTarget = SwipeTarget;
